/**
 * useColdStartRecovery Hook
 *
 * On mount, checks if the server has an active generation lock (e.g. user closed
 * the tab/browser during generation). If so, shows the loading UI and polls for
 * results instead of requiring the user to tap Generate and hit a 429.
 */

import { API_ENDPOINTS, apiUrl } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';

interface UseColdStartRecoveryProps {
  onPicksRecovered: (picks: unknown, generationId: string) => void;
  onGenerationResumed: (startedAt: number | null) => void;
  onGenerationEnded: () => void;
}

interface ActiveResponse {
  active: boolean;
  startedAt: number | null;
}

interface UndeliveredResponse {
  undelivered: {
    generationId: string;
    picks: unknown;
    createdAt: string;
    analysisMode: string;
  } | null;
}

const POLL_INTERVAL_MS = 3000;

/**
 * Checks for an in-progress generation on mount and polls for results.
 * Handles three cold-start scenarios:
 * 1. Lock active + picks ready → apply immediately
 * 2. Lock active + no picks yet → show loader, poll every 3s
 * 3. Lock released with no picks → generation failed, do nothing
 */
export function useColdStartRecovery({
  onPicksRecovered,
  onGenerationResumed,
  onGenerationEnded,
}: UseColdStartRecoveryProps) {
  const { authFetch } = useAuth();
  const hasRunRef = useRef(false);

  useEffect(() => {
    // Only run once per mount
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    const checkActiveGeneration = async () => {
      try {
        const activeRes = await authFetch(apiUrl(API_ENDPOINTS.GENERATIONS_ACTIVE));
        if (!activeRes.ok) return;

        const { active, startedAt } = (await activeRes.json()) as ActiveResponse;
        if (!active || cancelled) return;

        // Lock is active — check for already-completed picks first
        const undeliveredRes = await authFetch(apiUrl(API_ENDPOINTS.GENERATIONS_UNDELIVERED));
        if (undeliveredRes.ok) {
          const data = (await undeliveredRes.json()) as UndeliveredResponse;
          if (data.undelivered?.picks) {
            onPicksRecovered(data.undelivered.picks, data.undelivered.generationId);
            toast.success('Recovered your picks from earlier!', {
              duration: 4000,
              icon: '🔄',
            });
            // Confirm delivery
            authFetch(apiUrl(API_ENDPOINTS.GENERATIONS_CONFIRM), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ generationId: data.undelivered.generationId }),
            }).catch(() => {});
            return;
          }
        }

        // Generation still running — show loader and poll
        if (cancelled) return;
        onGenerationResumed(startedAt);

        const poll = async () => {
          if (cancelled) return;
          try {
            // Check for completed picks
            const res = await authFetch(apiUrl(API_ENDPOINTS.GENERATIONS_UNDELIVERED));
            if (res.ok) {
              const data = (await res.json()) as UndeliveredResponse;
              if (data.undelivered?.picks) {
                onPicksRecovered(data.undelivered.picks, data.undelivered.generationId);
                onGenerationEnded();
                toast.success('Recovered your picks from earlier!', {
                  duration: 4000,
                  icon: '🔄',
                });
                authFetch(apiUrl(API_ENDPOINTS.GENERATIONS_CONFIRM), {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ generationId: data.undelivered.generationId }),
                }).catch(() => {});
                return;
              }
            }

            // Check if lock is still held
            const lockRes = await authFetch(apiUrl(API_ENDPOINTS.GENERATIONS_ACTIVE));
            if (lockRes.ok) {
              const { active: stillActive } = (await lockRes.json()) as ActiveResponse;
              if (!stillActive) {
                // Lock released but no picks — generation failed server-side
                onGenerationEnded();
                return;
              }
            }

            // Still running — poll again
            if (!cancelled) {
              pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
            }
          } catch {
            onGenerationEnded();
          }
        };

        pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        // Silent failure — user can generate manually
      }
    };

    checkActiveGeneration();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [onPicksRecovered, onGenerationResumed, onGenerationEnded, authFetch]);
}
